/*
 * Copyright © 2025 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {ChangeDetectionStrategy, Component, inject, input, signal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {type ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReportTemplateApi} from '@dv/shared/backend/api/report-template.api';
import {JaxReportTemplate} from '@dv/shared/backend/model/jax-report-template';
import {ReportType} from '@dv/shared/backend/model/report-type';
import {FunctionType, isNullish} from '@dv/shared/code';
import {TranslocoModule} from '@jsverse/transloco';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {map, switchMap} from 'rxjs';
import {
    ReportTemplateOutdatedIconComponent,
} from '../report-template-outdated-icon/report-template-outdated-icon.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'dv-report-template-selector',
    imports: [
        FormsModule,
        TranslocoModule,
        TooltipModule,
        ReportTemplateOutdatedIconComponent,
    ],
    templateUrl: './report-template-selector.component.html',
    styleUrl: './report-template-selector.component.scss',
    providers: [
        {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: ReportTemplateSelectorComponent},
    ],
})
export class ReportTemplateSelectorComponent implements ControlValueAccessor {

    public reportType = input.required<ReportType>();

    private reportTemplateApi = inject(ReportTemplateApi);

    public reportTemplates = toSignal(toObservable(this.reportType).pipe(
        switchMap(reportType => this.reportTemplateApi.getAllByType$({reportType})),
        map(result => result.reportTemplates.toSorted((a, b) => a.name.localeCompare(b.name))),
    ), {initialValue: []});

    public selectedReportTemplate = signal<JaxReportTemplate | undefined>(undefined);

    public onChange?: (model?: JaxReportTemplate) => unknown;
    public onTouched?: FunctionType;
    public isDisabled = signal(false);

    public compareById(a: any, b: any): boolean {
        if (isNullish(a) || isNullish(b)) {
            return a === b;
        }

        return a.id === b.id;
    }

    public templateChanged(): void {
        this.onChange?.(this.selectedReportTemplate());
    }

    public registerOnChange(fn: FunctionType): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: FunctionType): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    public writeValue(obj?: JaxReportTemplate): void {
        this.selectedReportTemplate.set(obj);
    }
}
